import { FileUpload } from "@/components/ui/file-upload";
import { AutocompleteField } from "@/features/FilterTable/AutocompleteField";
import { DocumentIcon } from "@heroicons/react/24/outline";

import { getAuthorized, postFormAuthorized } from "@/lib/fetch";
import { Button } from "@heroui/react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ConfigSchemaType, UploadSchemaType } from "./filter";
import { Clients } from "../../../lib/clients";
import { ClientTables } from "@/lib/clientTables";
import {
  getSelectedConfig,
  getTemplateFileName,
} from "@/lib/utils/data_upload/uploadProviderConfig";
import { DataUploadConfigDetails } from "@/features/DataUpload/DataUploadConfigDetails";

type Mode = "create" | "modify";

type M2DataUploadNewProps = {
  setIsOpen: (val: boolean) => void;
  files: File[];
  setFiles: (files: File[]) => void;
  selectedConfig: string | null;
  setSelectedConfig: (config: string | null) => void;
  mode?: Mode;
  fileId?: number;
  onSuccess?: () => Promise<void> | void;
};

export function M2DataUploadNew({
  setIsOpen,
  files,
  setFiles,
  selectedConfig,
  setSelectedConfig,
  mode = "create",
  fileId,
  onSuccess,
}: M2DataUploadNewProps) {
  const { t } = useTranslation(["tables"]);

  const [configs, setConfigs] = useState<ConfigSchemaType[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { setValue, watch } = useForm<UploadSchemaType>();
  const configValue = watch("config_name");

  // 🔄 Fetch available configs on mount
  useEffect(() => {
    setSelectedConfig(configValue || null);
  }, [configValue, setSelectedConfig]);

  useEffect(() => {
    if (selectedConfig) {
      setValue("config_name", selectedConfig);
    }
  }, [selectedConfig, setValue]);

  useEffect(() => {
    const fetchConfigs = async () => {
      try {
        const res = await getAuthorized(
          `${import.meta.env.VITE_BACKEND_URL}/${Clients.M2}/m2_data_upload/configs`,
        );
        const data = await res.json();
        setConfigs(data); // expects an array of strings
      } catch (err) {
        console.error("Error fetching configs:", err);
        setError(t("data_upload.errors.load_configurations"));
      }
    };

    fetchConfigs();
  }, []);

  const handleEdit = async () => {
    if (!files.length || files.length !== 1) {
      setError(t("data_upload.errors.select_single_file"));
      return;
    }

    setIsSubmitting(true);
    setError(null);

    const formData = new FormData();
    formData.append("file", files[0]);

    try {
      const result = await postFormAuthorized(
        `${import.meta.env.VITE_BACKEND_URL}/${Clients.M2}/m2_data_upload/edit/${fileId}`, // adjust endpoint if needed
        formData,
      );

      await onSuccess?.();
      setIsOpen(false);
      toast.success(t("data_upload.success.file_modified"));
      console.log(result);
      setFiles([]);
    } catch (err: any) {
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        "Une erreur est survenue lors de la modification.";
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedConfig) {
      setError(t("data_upload.errors.select_configuration"));
      return;
    }

    if (!files.length) {
      setError(t("data_upload.errors.select_files"));
      return;
    }

    setIsSubmitting(true);
    setError(null);

    const selectedConfigObj = configs.find((c) => c.code === selectedConfig);

    if (!selectedConfigObj) {
      setError(t("data_upload.errors.invalid_configuration"));
      return;
    }

    if (!selectedConfigObj.mime_type) {
      setError(t("data_upload.errors.missing_file_type"));
      return;
    }

    const formData = new FormData();
    files.forEach((file) => formData.append("files", file));
    formData.append("config_name", selectedConfig);
    formData.append("config_id", String(selectedConfigObj.id));

    try {
      const result = await postFormAuthorized(
        `${import.meta.env.VITE_BACKEND_URL}/${Clients.M2}/m2_data_upload/upload`,
        formData,
      );

      await onSuccess?.();
      setIsOpen(false);
      toast.success(t("data_upload.success.files_sent"));
      console.log(result);
      setFiles([]);
      setSelectedConfig(null);
    } catch (err: any) {
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        "Une erreur est survenue lors de l'envoi.";
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = async () => {
    setIsOpen(false);
    setFiles([]);
    setSelectedConfig(null);
  };

  const selectedConfigObj = getSelectedConfig(configs, selectedConfig);
  const acceptedTypes = selectedConfigObj?.mime_type
    ? [selectedConfigObj.mime_type]
    : [];
  const templateFileName = getTemplateFileName(
    selectedConfigObj?.template_s3_key,
  );
  const usageDescription = selectedConfigObj?.usage_description?.trim() ?? "";
  const hasTemplate = Boolean(selectedConfigObj?.template_s3_key);
  const hasConfigDetails = acceptedTypes.length > 0 || hasTemplate;
  const fileTypesLabel = t("data_upload.labels.file_types", { ns: "tables" });

  return (
    <div className="flex justify-center items-start w-full px-4 py-6">
      <div className="flex flex-col w-full max-w-card rounded-lg p-6 gap-4 bg-white">
        {mode === "create" ? (
          <AutocompleteField
            field={{
              name: "config_name",
              label: "Configuration",
              type: "autocomplete",
              options: configs.map((c) => ({
                value: c.code,
                label: c.code,
              })),
            }}
            setValue={setValue as any}
            watch={watch}
          />
        ) : (
          ""
        )}
        {hasConfigDetails && (
          <div className="flex flex-col gap-2 text-sm text-gray-600">
            {acceptedTypes?.length > 0 && (
              <Button
                size="sm"
                variant="light"
                color="primary"
                disableRipple
                tabIndex={-1}
                className="shrink-0 min-w-0 pointer-events-none justify-start"
              >
                <DocumentIcon className="size-4 mr-1" />
                {`${fileTypesLabel} ${acceptedTypes.join(", ")}`}
              </Button>
            )}
            {hasTemplate && selectedConfigObj?.id && (
              <DataUploadConfigDetails
                configId={selectedConfigObj.id}
                client={Clients.M2}
                table={ClientTables.M2_DATA_UPLOAD}
                templateFileName={templateFileName}
                usageDescription={usageDescription}
              />
            )}
          </div>
        )}
        <FileUpload
          files={files}
          setFiles={setFiles}
          setError={setError}
          acceptedTypes={acceptedTypes}
          maxFiles={mode === "modify" ? 1 : undefined}
        />
        <footer className="flex justify-end gap-2">
          <Button
            color="primary"
            radius="full"
            size="sm"
            onPress={mode === "create" ? handleSubmit : handleEdit}
            disabled={isSubmitting || !files?.length}
            className="disabled:opacity-50"
          >
            {isSubmitting ? "Envoi..." : "Valider"}
          </Button>
          <Button
            color="primary"
            radius="full"
            variant="flat"
            size="sm"
            onPress={handleReset}
          >
            Annuler
          </Button>
        </footer>
        {error && <p className="text-sm text-red-500">{error}</p>}
      </div>
    </div>
  );
}
